import { fetchEndUserDashboardState } from 'services/api/endUserDashboardApi';
import type { EndUserDashboardState } from 'services/api/endUserDashboardApi.data';

type DashboardStateCache = {
  authKey: string;
  promise: Promise<EndUserDashboardState>;
};

let dashboardStateCache: DashboardStateCache | null = null;

export const buildEndUserAuthKey = (euId: string, idToken: string): string => `${euId}:${idToken}`;

const clearCacheForAuthKey = (authKey: string): void => {
  if (dashboardStateCache?.authKey === authKey) {
    dashboardStateCache = null;
  }
};

/** Clears in-flight / resolved dashboard state cache (for tests and logout flows). */
export const resetEndUserDashboardStateCache = (): void => {
  dashboardStateCache = null;
};

/**
 * Loads GET /eu/{euId}/state with per-session deduplication so layout, resolver, and home
 * share one network request for the same credentials.
 */
export const loadEndUserDashboardState = (
  euId: string,
  idToken: string,
  options?: { force?: boolean }
): Promise<EndUserDashboardState> => {
  const authKey = buildEndUserAuthKey(euId, idToken);

  if (!options?.force && dashboardStateCache?.authKey === authKey) {
    return dashboardStateCache.promise;
  }

  const promise = fetchEndUserDashboardState(euId, idToken);
  dashboardStateCache = { authKey, promise };

  void promise.catch(() => {
    clearCacheForAuthKey(authKey);
  });

  return promise;
};
